import { useState, useEffect, memo } from "react";
import { v4 as uuidv4 } from "uuid";
import axios from "../../customAxios";
import {
  Button,
  Box,
  Drawer,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  Chip,
  Typography,
  TextField,
} from "@mui/material";

import {
  getDeliveryStatus,
  getDefaultWebhookAttrs,
} from "../../utils/functions";
import KeyValueEditor from "../KeyValueEditor";
import { useKeyValueStore } from "../../hooks/useKeyValueStore";

const WebhookForm = ({
  selectedWebhook,
  handleSnack,
  drawerOpen,
  toggleDrawer,
  isEditing,
  setWebhooks,
}) => {
  const [defaultWebhookAttrs] = useState(getDefaultWebhookAttrs());
  const [statuses] = useState(getDeliveryStatus());
  const [substatuses, setSubStatuses] = useState([]);
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("");
  const [substatus, setSubStatus] = useState("");
  const [webhook, setWebhook] = useState({
    url: "",
    status: "",
    substatus: "",
    headers: {},
    body: {},
  });

  const headersStore = useKeyValueStore({
    "{{content_type}}": "application/json",
    "{{authorization}}": "",
  });

  const bodyStore = useKeyValueStore({
    "{{order_id}}": "{{order_id}}",
    "{{reference_id}}": "{{reference_id}}",
    "{{status}}": "{{status}}",
    "{{substatus}}": "{{substatus}}",
    "{{status_timestamp}}": "{{status_timestamp}}",
    "{{comments}}": "{{comments}}",
    "{{recipients}}": "{{recipients}}",
    "{{address}}": "{{address}}",
    "{{commune}}": "{{commune}}",
    "{{region}}": "{{region}}",
    "{{url_images}}": "{{url_images}}",
  });

  useEffect(() => {
    const setUseKeyStore = (store) => {
      return Object.entries(store).map(([key, value]) => ({
        id: uuidv4(),
        key: key,
        value: value,
      }));
    };

    if (isEditing) {
      setUrl(selectedWebhook.url);
      setStatus(selectedWebhook.status);
      setSubStatus(selectedWebhook.substatus);
      setWebhook(selectedWebhook);
      headersStore.setStore(setUseKeyStore(selectedWebhook.headers));
      bodyStore.setStore(setUseKeyStore(selectedWebhook.body));
    }
  }, [isEditing]);

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
    const currentStatus = statuses.find(
      (status) => status.system_name === event.target.value
    );
    setSubStatuses(currentStatus.substatus);
    setWebhook({
      url: "",
      status: event.target.value,
      headers: headersStore.store,
      body: bodyStore.store,
    });
  };

  const saveWebhook = async (payload) => {
    const url = isEditing
      ? `companies/setting_webhooks/update/${webhook.id}/`
      : "companies/setting_webhooks/create";
    const method = isEditing ? "patch" : "post";

    try {
      const { data } = await axios[method](
        url,
        { setting_webhook: payload }
      );
      if (data) {
        setWebhooks((prev) =>
          isEditing
            ? prev.map((w) => (w.id === data.id ? data : w))
            : [data, ...prev]
        );
        toggleDrawer(false)();
      } else {
        handleSnack(true, "Error al guardar los datos, código esperado 200");
      }
    } catch (error) {
      console.error(error);
      handleSnack(
        true,
        error?.response?.data?.setting_webhook ||
          "Error al guardar los datos, código esperado 200"
      );
    }
  };

  const handleSaveConfig = (event) => {
    event.preventDefault();

    if (status === "" || url === "" || substatus === "") {
      handleSnack(true, "Estado/SubEstado y URL son datos requeridos");
      return;
    }

    const payload = {
      ...webhook,
      url: url,
      status: status,
      substatus: substatus,
      headers: headersStore.getObjectStore(),
      body: bodyStore.getObjectStore(),
    };
    setWebhook(payload);
    saveWebhook(payload);
  };

  const handleDrawerClose = () => {
    setUrl("");
    setStatus("");
    setSubStatus("");
    setWebhook({
      url: "",
      status: "",
      substatus: "",
      headers: {},
      body: {},
    });
    toggleDrawer(false)();
  };

  return (
    <Drawer anchor={"right"} open={drawerOpen} onClose={handleDrawerClose}>
      <Box sx={{ width: 600 }} role="presentation">
        <>
          <Grid
            container
            direction="column"
            sx={{
              justifyContent: "flex-start",
              alignItems: "stretch",
              pl: 6,
              pr: 6,
              pt: isEditing ? 6 : 4,
            }}
            component="form"
            onSubmit={handleSaveConfig}
            noValidate
          >
            <>
              <Link
                href={"ADD_LINK_TO_TUTORIAL_PATH"}
                variant="body1"
                target="_blank"
                underline="hover"
                sx={{
                  "&:hover": {
                    textDecoration: "underline !important",
                  },
                  textDecoration: "underline !important",
                }}
              >
                Configura tu webhook! aquí te dejamos un tutorial para
                descargar.
              </Link>
              {!isEditing ? (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="status">Seleccionar Estado</InputLabel>
                    <Select
                      labelId="status"
                      id="status"
                      value={status}
                      label="Seleccionar Estado"
                      onChange={handleStatusChange}
                    >
                      {statuses.map((status) => (
                        <MenuItem
                          value={status.system_name}
                          key={`status-${status.system_name}-${status.id}`}
                        >
                          {status.user_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {substatuses.length > 0 ? (
                    <FormControl fullWidth>
                      <InputLabel id="substatus">
                        Seleccionar Sub Estado
                      </InputLabel>
                      <Select
                        labelId="substatus"
                        id="substatus"
                        value={substatus}
                        label="Seleccionar Sub Estado"
                        onChange={(event) => setSubStatus(event.target.value)}
                      >
                        {substatuses.map((substatus) => (
                          <MenuItem
                            value={substatus.system_name}
                            key={`substatus-${substatus.system_name}-${substatus.id}`}
                          >
                            {substatus.user_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                </>
              ) : null}

              {webhook?.status && substatus ? (
                <>
                  <Grid size={16} sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="URL destino"
                      variant="outlined"
                      value={url}
                      onChange={(event) => setUrl(event.target.value)}
                      size="small"
                    />
                  </Grid>
                  <Grid size={16} sx={{ mb: 2 }}>
                    <KeyValueEditor
                      title="Agregar cabeceras"
                      store={headersStore.store}
                      addCustomValue={(key, value, handleSnack) =>
                        headersStore.addCustomValue(key, value, handleSnack)
                      }
                      updateKey={headersStore.updateKey}
                      updateValue={headersStore.updateValue}
                      removeKey={headersStore.removeKey}
                      handleSnack={handleSnack}
                    />
                  </Grid>
                  <Grid size={16} sx={{ mb: 2 }}>
                    <KeyValueEditor
                      title="Agregar cuerpo"
                      store={bodyStore.store}
                      addCustomValue={(key, value, handleSnack) =>
                        bodyStore.addCustomValue(key, value, handleSnack)
                      }
                      updateKey={bodyStore.updateKey}
                      updateValue={bodyStore.updateValue}
                      removeKey={bodyStore.removeKey}
                      handleSnack={handleSnack}
                    />
                    <div className="staked-div-separator"></div>
                    <Typography className="stacked-label" variant="h6">
                      Valores disponible de sistema
                    </Typography>
                    {defaultWebhookAttrs.map((attr, index) => (
                      <Chip
                        key={`${attr}-${index}`}
                        label={attr}
                        color="primary"
                        className="staked-chip"
                        variant="outlined"
                      />
                    ))}
                  </Grid>

                  <Grid size={16}>
                    <Button
                      type="submit"
                      variant="outlined"
                      fullWidth
                      sx={{
                        mt: 4,
                        mb: 4,
                      }}
                    >
                      {isEditing ? "Actualizar Webhook" : "Agregar Webhook"}
                    </Button>
                  </Grid>
                </>
              ) : null}
            </>
          </Grid>
        </>
      </Box>
    </Drawer>
  );
};

export default memo(WebhookForm);
